import styled, { css } from 'styled-components';

import { PageType } from '../shared/constants/Contentful.constants';

import Radiusses from '../tokens/Radiusses';

import mq from './breakpoints';
import Colors from './colors';
import Spacings from './Spacings';

export const H1Css = css`
  font-family: ${({ theme }) => theme.fontCondensed};
  font-size: ${({ theme }) => theme.h1?.fontSize?.S || '40px'};
  font-style: normal;
  font-weight: 400;
  line-height: ${({ theme }) => theme.h1?.lineHeight?.S || '48px'};
  margin: 0 0 ${Spacings.XS} 0;

  ${mq.from.breakpoint.L`
    font-size: ${({ theme }: { theme: any }) =>
      theme.h1?.fontSize?.L || '56px'};
    line-height: ${({ theme }: { theme: any }) =>
      theme.h1?.lineHeight?.L || '64px'};
  `}
`;

export const H1SemiBoldCss = css`
  font-family: ${({ theme }) => theme.fontSemibold};
  font-size: ${({ theme }) => theme.h1?.fontSize?.S || '40px'};
  font-style: normal;
  font-weight: 700;
  line-height: ${({ theme }) => theme.h1?.lineHeight?.S || '48px'};
  margin: 0 0 ${Spacings.XS} 0;

  ${mq.from.breakpoint.L`
    font-size: ${({ theme }: { theme: any }) =>
      theme.h1?.fontSize?.L || '56px'};
    line-height: ${({ theme }: { theme: any }) =>
      theme.h1?.lineHeight?.L || '64px'};
  `}
`;

export const H2Css = css`
  font-family: ${({ theme }) => theme.fontCondensed};
  font-size: ${({ theme }) => theme.h2?.fontSize?.S || '28px'};
  font-style: normal;
  font-weight: 400;
  letter-spacing: 0em;
  line-height: ${({ theme }) => theme.h2?.lineHeight?.S || '32px'};
  margin-bottom: ${Spacings.S};
  word-break: break-word;

  ${mq.from.breakpoint.L`
    font-size: ${({ theme }: { theme: any }) =>
      theme.h2?.fontSize?.L || '32px'};
    line-height: ${({ theme }: { theme: any }) =>
      theme.h2?.lineHeight?.L || '40px'};
  `}
`;

export const H3Css = css`
  font-family: ${({ theme }) => theme.fontCondensed};
  font-size: ${({ theme }) => theme.h3?.fontSize?.S || '20px'};
  font-style: normal;
  font-weight: 400;
  line-height: ${({ theme }) => theme.h3?.lineHeight?.S || '24px'};

  ${mq.from.breakpoint.L`
    font-size: ${({ theme }: { theme: any }) =>
      theme.h3?.fontSize?.L || '24px'};
    line-height: ${({ theme }: { theme: any }) =>
      theme.h3?.lineHeight?.L || '32px'};
  `}
`;

export const H1 = styled.h1`
  color: inherit;
  ${H1Css};
`;

export const H2 = styled.h2`
  color: inherit;
  ${H2Css}
`;

export const UnstyledH2 = styled(H2)`
  margin: 0;
`;

export const ProductPageH2 = styled.h2`
  font-family: ${({ theme }) => theme.fontCondensed};
  font-size: 32px;
  font-style: normal;
  font-weight: normal;
  line-height: 40px;
  margin: 0 0 ${Spacings.XL};

  ${mq.from.breakpoint.L`
    font-size: 48px;
    line-height: 56px;
    margin: 0 0 64px;
    text-align: center;
  `}
`;

export const HeroTitle = styled.h1`
  ${H1Css};

  ${mq.from.breakpoint.L`
    font-size: 42px;
    line-height: 58px;
  `}

  ${mq.from.breakpoint.XL`
    font-size: ${({ theme }: { theme: any }) => theme.h1.fontSize.L};
    line-height: ${({ theme }: { theme: any }) => theme.h1.lineHeight.L};
  `}
`;

export const H3 = styled.h3`
  color: inherit;
  ${H3Css}
`;

export const UnstyledH3 = styled(H3)`
  margin: 0;
`;

export const H4 = styled.h4`
  font-family: ${({ theme }) => theme.fontRegular};
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  margin: 0 0 ${Spacings.XS} 0;

  ${mq.from.breakpoint.L`
    font-size: 20px;
    line-height: 24px;
  `}
`;

export const UnstyledH4 = styled(H4)`
  margin: 0;
`;

export const H5 = styled.h5`
  color: ${Colors.abnMutedGreen400};
  font-family: ${({ theme }) => theme.fontRegular};
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`;

export const IntroText = styled.span`
  color: inherit;
  font-family: ${({ theme }) => theme.fontRegular};
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
  margin: 0;

  h2 {
    color: inherit;
    font-family: ${({ theme }) => theme.fontCondensed};
    font-size: 28px;
    font-weight: 300;
    line-height: 32px;
    margin-block-end: 0px;
    margin-block-start: 0px;
    margin-bottom: ${Spacings.XS};
    margin-top: 0;
  }
`;

export const BodyCSS = css`
  color: ${({ theme }) => theme.colors.abnNeutral610};
  font-family: ${({ theme }) => theme.fontRegular};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
`;

export const Body = styled.div<{ hasCustomLinkStyles?: boolean }>`
  ${BodyCSS}

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    color: ${({ theme }) => theme.colors.black};

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  p {
    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }
  }

  p ~ ul,
  p ~ ol {
    padding-top: ${Spacings.S};
  }

  ul,
  ol {
    padding-top: 0;
    padding-bottom: 0;
    margin-bottom: ${Spacings.S};

    &:first-child {
      margin-top: 0;
    }

    &:last-child {
      margin-bottom: 0;
    }

    > li:last-child {
      margin-bottom: 0;
    }
  }

  h1 {
    ${H1Css}
  }

  h2 {
    ${H2Css}
  }

  h3 {
    ${H3Css}
  }

  ${({ hasCustomLinkStyles, theme }) =>
    !hasCustomLinkStyles
      ? `a {
    color: ${theme.colors.abnGreen500};
    &:hover {
      color: ${theme.colors.abnGreen600};
    }
    &:active {
      color: ${theme.colors.abnGreen670};
    }
  }`
      : ''}
`;

export const ListTitle = styled(Body)`
  font-family: ${({ theme }) => theme.fontSemibold};
`;

export const SmallTextCss = css`
  font-family: ${({ theme }) => theme.fontRegular};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
`;

export const SmallText = styled.div`
  color: inherit;
  ${SmallTextCss}
`;

export const LabelStyles = css`
  font-family: ${({ theme }) => theme.fontSemibold};
  font-size: 14px;
  font-style: normal;
  font-weight: ${({ theme }) => theme.label.fontWeight};
  line-height: 20px;

  input,
  textarea {
    display: block;
  }
`;

export const Label = styled.label`
  color: ${Colors.abnMutedGreen400};
  display: block;
  font-family: ${({ theme }) => theme.fontSemibold};
  font-size: 14px;
  font-style: normal;
  font-weight: ${({ theme }) => theme.label.fontWeight};
  line-height: 20px;

  input,
  textarea {
    display: block;
  }
`;

export const LabelWrapper = styled.div`
  column-gap: 4px;
  display: inline-flex;
  justify-content: flex-start;
  min-height: 20px;
  width: 100%;
`;

export const Legend = styled.legend`
  color: ${Colors.abnMutedGreen400};
  display: block;
  font-family: ${({ theme }) => theme.fontSemibold};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 20px;
  padding: 0;
`;

export const LabelLight = styled.span`
  font-weight: 400;
`;

export const HeroSubtitleCss = css`
  color: inherit;
  font-family: ${({ theme }) => theme.fontCondensed};
  font-size: 28px;
  font-weight: 300;
  line-height: 32px;
  margin-block-end: 0px;
  margin-block-start: 0px;
  margin-bottom: ${Spacings.XS};
  margin-top: 0;

  ${mq.from.breakpoint.L`
    font-weight: 400;
    font-size: 32px;
    line-height: 40px;
  `}
`;

export const HeroSubtitle = styled.h2`
  ${HeroSubtitleCss}
`;

export const IntroCopyStyles = css`
  color: inherit;
  font-family: ${({ theme }) => theme.fontRegular};
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 32px;
`;

export const IntroCopyStylesBold = css`
  color: inherit;
  font-family: ${({ theme }) => theme.fontSemibold};
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 32px;
`;

export const ButtonLabel = css`
  font-size: 16px;
  font-style: normal;
  font-weight: normal;
  word-break: break-word;
`;

export const SpanBold = styled.span`
  font-family: ${({ theme }) => theme.fontCondensed};
  font-weight: 700;
`;

export const AbsoluteTop = css`
  bottom: 0;
  display: block;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;

export const DefaultHeaderTitle = styled(H1)`
  margin: 88px auto ${Spacings.M};
  text-align: center;
  width: 100%;

  ${mq.from.breakpoint.M`
    max-width: 80vw;
    margin: 64px auto 48px;
  `}

  ${mq.from.breakpoint.L`
    margin: 96px auto 48px;
    max-width: 60vw;
  `}

  ${mq.from.breakpoint.XL`
    font-size: 48px;
    line-height: 56px;
  `}
`;

export const StyledContent = styled.div<{ type?: string }>`
  align-items: center;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  margin-bottom: ${Spacings.XXXL};
  width: inherit;

  ${mq.from.breakpoint.M`
    margin-bottom: 140px;
  `}

  ${mq.from.breakpoint.L`
    margin-bottom: 180px;
  `}

  > * {
    margin-top: ${Spacings.XXXL};

    ${mq.from.breakpoint.M`
      margin-top: 140px;
    `}

    ${mq.from.breakpoint.L`
      margin-top: 180px;
    `}

    &:first-child {
      ${({ type }) =>
        type === PageType.GATED_CONTACT
          ? css`
              margin-top: 0;

              ${mq.from.breakpoint.L`
                margin-top: 140px;
              `}
            `
          : css`
              margin-top: ${Spacings.XXXL};

              ${mq.from.breakpoint.L`
                margin-top: 180px;
              `}
            `}
    }
  }
`;

export const DecimalLabel = styled.span`
  font-size: 0.66em;
  position: relative;
`;

export const CardTitleCss = css`
  font-size: 24px;
  font-style: normal;
  font-weight: normal;
  line-height: 32px;

  ${mq.from.breakpoint.L`
    font-size: 32px;
    line-height: 40px;
  `}
`;

export const Divider = styled.hr`
  background-color: #c0d1d0;
  border: none;
  height: 1px;
  margin: 0;
  width: 100%;
`;

export const UnstyledUL = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const UnstyledLI = styled.li`
  margin: 0;
  padding: 0;
`;

export const CtaWrapper = styled.div`
  display: grid;
  gap: ${Spacings.S};
  grid-template-columns: repeat(1, 1fr);

  ${mq.from.breakpoint.M`
    grid-template-columns: repeat(2, 1fr);
  `}
`;

export const ListWrapper = styled.div`
  background-color: ${Colors.abnMutedGreen50};
  border-radius: ${Radiusses.L};
  display: grid;
  gap: ${Spacings.S};
  grid-template-columns: repeat(1, 1fr);
  padding: ${Spacings.S};

  ${mq.from.breakpoint.M`
    grid-template-columns: repeat(2, 1fr);
  `}
`;

export const LinksWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.XS};
`;

export const lineClampStyles = css`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`;

export const lineClampStylesLarge = css`
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  display: -webkit-box;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
`;

export const Blockquote = styled.blockquote`
  color: ${({ theme }) => theme.colors.black};
  font-family: ${({ theme }) => theme.fontCondensed};
  font-size: ${({ theme }) => theme.blockquote.fontSize.S};
  line-height: ${({ theme }) => theme.blockquote.lineHeight.S};
  width: 100%;

  > p {
    display: inline;
  }

  ${mq.from.breakpoint.M`
    font-size: ${({ theme }) => theme.blockquote.fontSize.L};
    line-height: ${({ theme }) => theme.blockquote.lineHeight.L};
  `}
`;
